import React from "react";
import { Box, HStack, Heading, Text } from "@chakra-ui/react";

// Plan views size themselves against the content area, not the viewport. The
// narrative panel can be open, collapsed, or dragged to a different width, so
// the window tells you very little about how much room a view actually has.
// `contentPanelWidth` is that real width, supplied by PlanPage and by
// SharedPlanViewer.
export const PAGE_NARROW_BREAKPOINT = 900;

// Shared visual language for contained narrative surfaces. Brief establishes
// the compact reading column; Strategy/Decisions establish the dark-card tone.
export const COMPACT_CONTENT_MAX_WIDTH = "760px";
export const ACCENT_CARD_BACKGROUND = "var(--brand-accent-card-bg, #2D3748)";
export const ACCENT_CARD_TEXT = "var(--brand-text-on-accent-card, #fff)";
export const ACCENT_CARD_CONTENT_PROPS = {
  // Matches the original Decisions verdict's combined Card + inner Box inset.
  // Explicit values avoid undefined Chakra spacing tokens being treated as px.
  px: { base: "32px", md: "52px" },
  py: { base: "36px", md: "48px" },
};

export const AccentCardLabel = ({ children, ...rest }) => (
  <Text
    fontSize="sm"
    fontWeight="semibold"
    color="whiteAlpha.600"
    textTransform="uppercase"
    letterSpacing="wider"
    lineHeight="1.3"
    {...rest}
  >
    {children}
  </Text>
);

export const isNarrowPanel = (contentPanelWidth = 1000) =>
  contentPanelWidth < PAGE_NARROW_BREAKPOINT;

/**
 * The standard page container for a plan view: one max width and one set of
 * responsive paddings, so pages stop drifting apart every time a new one is
 * added.
 *
 * Every prop passes through to the underlying Box, so a view that genuinely
 * needs to differ can say so explicitly and visibly:
 *   <PageShell maxW="none">        full-bleed content (timelines, calendars)
 *   <PageShell pb="120px">         extra room for a floating footer
 */
const PageShell = ({ contentPanelWidth = 1000, children, ...rest }) => {
  const isNarrow = isNarrowPanel(contentPanelWidth);
  return (
    <Box
      pt={isNarrow ? 4 : 20}
      pr={isNarrow ? 4 : 32}
      pb={isNarrow ? 4 : 20}
      pl={isNarrow ? 4 : 32}
      maxW="1400px"
      mx="auto"
      {...rest}
    >
      {children}
    </Box>
  );
};

export default PageShell;

/**
 * The page title for a plan view. One size, one weight, one colour, one heading
 * level, so a new page cannot quietly invent its own.
 *
 * It is deliberately just the heading. Each page keeps its own header row and
 * its own actions, because those genuinely differ: Product Strategy has History
 * and Generate New, Competitor Analysis adds copy and download, Decisions has
 * Add Learning. Only the title itself is shared.
 */
export const PageTitle = ({ contentPanelWidth = 1000, children, ...rest }) => (
  <Heading
    as="h1"
    fontWeight="bold"
    fontSize={isNarrowPanel(contentPanelWidth) ? "xl" : "3xl"}
    color="brand.400"
    {...rest}
  >
    {children}
  </Heading>
);

/** Optional supporting line under a PageTitle, for pages that explain themselves. */
export const PageSubtitle = ({ children, ...rest }) => (
  <Text fontSize="sm" color="gray.600" lineHeight="1.6" maxW="720px" {...rest}>
    {children}
  </Text>
);

/**
 * A labelled rule that separates the sections of a page, with the label set into
 * the line. Padding and stickiness pass through for pages that need them:
 *   <SectionDivider pb={3}>                 tighter gap under the label
 *   <SectionDivider position="sticky" ...>  label stays put while its section scrolls
 */
export const SectionDivider = ({ children, ...rest }) => (
  <HStack spacing={3} pt={6} pb={5} w="full" align="center" {...rest}>
    <Box flex="1" borderTop="1px solid" borderColor="gray.300" />
    <Text fontSize="xs" fontWeight="800" color="gray.600" lineHeight="1.2" flexShrink={0}>
      {children}
    </Text>
    <Box flex="1" borderTop="1px solid" borderColor="gray.200" />
  </HStack>
);
